import { useEffect, useState } from "react";
import { ExternalButton } from "../../components/Button";
import { Stat } from "../../components/Stat";
import { useConfig } from "../../hooks/useConfig";

/**
 * Market numbers for the last 24 hours, from GET /api/market (DexScreener behind
 * a one-minute cache). Facts about the past only. The one derived figure is the
 * pledge implied by that volume at the published creator tax, labelled as an
 * estimate, because the real number is whatever the fee claim says each quarter.
 */
interface Market {
  url: string;
  priceUsd: number | null;
  marketCapUsd: number | null;
  liquidityUsd: number | null;
  volume24hUsd: number | null;
  buys24h: number | null;
  sells24h: number | null;
  priceChange24hPct: number | null;
  fetchedAt: number;
}

const usd = (v: number | null, digits = 0): string =>
  v === null ? "..." : `$${new Intl.NumberFormat("en-US", { maximumFractionDigits: digits, minimumFractionDigits: digits }).format(v)}`;

const price = (v: number | null): string => {
  if (v === null) return "...";
  const digits = v >= 1 ? 2 : Math.min(10, Math.max(2, 2 - Math.floor(Math.log10(v))));
  return `$${v.toFixed(digits)}`;
};

export function MarketChart() {
  const { config } = useConfig();
  const url = config?.links?.dexscreener ?? null;
  const [market, setMarket] = useState<Market | null | undefined>(undefined);

  useEffect(() => {
    if (!url) return;
    let live = true;
    const load = () =>
      fetch("/api/market")
        .then((r) => (r.ok ? r.json() : { market: null }))
        .then((body: { market: Market | null }) => {
          if (live) setMarket(body.market);
        })
        .catch(() => {
          if (live) setMarket(null);
        });
    void load();
    const timer = window.setInterval(load, 60_000);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [url]);

  if (!url) return null;
  const creatorPct = config?.fees?.creatorTaxPct ?? null;
  const pledgePct = config?.fees?.pledgePctOfVolume ?? null;
  const volume = market?.volume24hUsd ?? null;
  const impliedPledge = volume !== null && pledgePct !== null ? (volume * pledgePct) / 100 : null;
  const trades = market && market.buys24h !== null && market.sells24h !== null ? market.buys24h + market.sells24h : null;

  return (
    <section className="shell section section--ruled" id="chart" aria-label="Market">
      <div className="stack stack-4">
        <div className="stack stack-2">
          <p className="eyebrow">Market, last 24 hours</p>
          <p className="tiny">
            From DexScreener, refreshed every minute. A record of the past, not a promise about the
            future.
          </p>
        </div>
        {market === null ? (
          <p className="body">Market data is not available right now.</p>
        ) : (
          <div className="grid-3">
            <Stat value={price(market?.priceUsd ?? null)} label="Price" />
            <Stat value={usd(market?.marketCapUsd ?? null)} label="Market cap" />
            <Stat value={usd(market?.liquidityUsd ?? null)} label="Liquidity, locked" />
            <Stat value={usd(volume)} label="Traded in 24h" />
            <Stat value={trades === null ? "..." : `${trades}`} label="Trades in 24h" />
            <Stat
              value={impliedPledge === null ? "..." : usd(impliedPledge)}
              label={pledgePct === null ? "Pledge from that volume" : `Pledge implied by that volume, at ${creatorPct}% creator tax, estimate`}
            />
          </div>
        )}
        <div>
          <ExternalButton href={url} variant="ghost">
            Open the chart on DexScreener
          </ExternalButton>
        </div>
      </div>
    </section>
  );
}
